import { useState } from "react";
import { toast } from "sonner";
import { AtSign, KeyRound, Loader2, Trash2 } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const PwInput = ({ label, value, onChange, testId, autoComplete }) => (
  <label className="block">
    <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    <input type="password" required data-testid={testId} value={value} autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full h-12 px-3 bg-white border border-slate-300 outline-none focus:border-slate-900 transition-colors" />
  </label>
);

const EmailForm = () => {
  const { user, updateUser } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-email", { current_password: pw, new_email: email });
      const { token, ...u } = data;
      updateUser(u, token);
      toast.success(`Login email changed to ${u.email}`);
      setEmail(""); setPw("");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-slate-300 p-5 space-y-4 fade-up" data-testid="change-email-form">
      <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
        <AtSign size={16} /><h2 className="font-bold text-base md:text-lg">Change login email</h2>
      </div>
      <label className="block">
        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">New email</span>
        <input type="email" required data-testid="new-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder={user?.email} autoComplete="email"
          className="mt-1.5 w-full h-12 px-3 bg-white border border-slate-300 outline-none focus:border-slate-900 transition-colors" />
      </label>
      <PwInput label="Current password" value={pw} onChange={setPw} testId="email-current-password-input" autoComplete="current-password" />
      {err && <p data-testid="change-email-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{err}</p>}
      <button data-testid="change-email-submit-btn" disabled={busy}
        className="w-full h-12 bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors disabled:opacity-60">
        {busy && <Loader2 className="animate-spin" size={16} />} Update email
      </button>
    </form>
  );
};

const ResetForm = () => {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (confirm.trim().toUpperCase() !== "DELETE") return setErr("Type DELETE to confirm.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", { current_password: pw, confirm });
      toast.success(`Deleted ${data.books} books, ${data.entries} entries and ${data.staff} staff`);
      setPw(""); setConfirm("");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-red-300 p-5 space-y-4 fade-up" data-testid="reset-form">
      <div className="flex items-center gap-2 pb-2 border-b border-red-200 text-red-800">
        <Trash2 size={16} /><h2 className="font-bold text-base md:text-lg">Delete all data</h2>
      </div>
      <p className="text-sm text-slate-600">Removes every book, entry, change history, share link and staff login. Your own login stays. This can't be undone.</p>
      <PwInput label="Current password" value={pw} onChange={setPw} testId="reset-password-input" autoComplete="current-password" />
      <label className="block">
        <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Type DELETE to confirm</span>
        <input required data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          className="mt-1.5 w-full h-12 px-3 bg-white border border-slate-300 outline-none focus:border-red-700 font-mono uppercase" />
      </label>
      {err && <p data-testid="reset-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{err}</p>}
      <button data-testid="reset-submit-btn" disabled={busy}
        className="w-full h-12 bg-red-700 text-white font-semibold flex items-center justify-center gap-2 hover:bg-red-800 transition-colors disabled:opacity-60">
        {busy && <Loader2 className="animate-spin" size={16} />} Delete everything
      </button>
    </form>
  );
};

export default function SettingsPage() {
  const { user, isAdmin } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("New password must be at least 8 characters.");
    if (pw !== pw2) return setErr("The new passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed");
      setCur(""); setPw(""); setPw2("");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div className="fade-up">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Account</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Settings</h1>
          <p className="text-sm text-slate-500 mt-3">Signed in as <span className="font-mono text-slate-900" data-testid="settings-email">{user?.email}</span></p>
        </div>
        <EmailForm />
        <form onSubmit={submit} className="bg-white border border-slate-300 p-5 space-y-4 fade-up" data-testid="change-password-form">
          <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
            <KeyRound size={16} /><h2 className="font-bold text-base md:text-lg">Change password</h2>
          </div>
          <PwInput label="Current password" value={cur} onChange={setCur} testId="current-password-input" autoComplete="current-password" />
          <PwInput label="New password (min 8 characters)" value={pw} onChange={setPw} testId="new-password-input" autoComplete="new-password" />
          <PwInput label="Confirm new password" value={pw2} onChange={setPw2} testId="confirm-password-input" autoComplete="new-password" />
          {err && <p data-testid="change-password-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{err}</p>}
          <button data-testid="change-password-submit-btn" disabled={busy}
            className="w-full h-12 bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={16} />} Update password
          </button>
        </form>
        {isAdmin && <ResetForm />}
      </main>
    </div>
  );
}
